import BaseService from "./BaseService";
import CustomerModel from "../core/models/Customer";
import { SessionCache } from "../helpers/cache";
import sortBy from "lodash/sortBy";
import { isArrayValid } from "../helpers/utility";
import { IInstallation } from "../core/contracts/IInstallation";
import User from "../core/models/User";
import UserService from "./UserService";
import NotificationRuleModel from "../core/models/NotificationRule";
import AllInstallationsFilter from "../core/models/AllInstallationsFilter";
import InstallationService from "./InstallationService";

export default class CustomerService extends BaseService {
  async fetchGroups(req: {
    idsToRequest: any[];
    useCustomers: any[];
    filters: AllInstallationsFilter;
    resultToAppend: CustomerModel[];
  }): Promise<any[] | undefined> {
    try {
      let params: Record<string, any> = {
        offset: 0,
        page_size: req.idsToRequest ? req.idsToRequest.length : 10,
      };

      const whereFilterMounted: Record<string, any>[] = [];
      if (req.idsToRequest) {
        if (isArrayValid(req.idsToRequest)) {
          whereFilterMounted.push({
            field: "installation_id",
            operator: "IN",
            values: req.idsToRequest.map((val) => {
              return { value: val.installation_id };
            }),
          });
        }
      }

      if (req?.filters?.installation_has_rtu) {
        whereFilterMounted.push({
          field: "has_rtu",
          operator: "=",
          value: req?.filters?.installation_has_rtu,
        });
      }

      const { filters } = req;

      const orderFilterMounted: Record<string, any>[] = [];
      if (filters?.order) {
        const key = Object.keys(filters.order)[0];
        orderFilterMounted.push({
          field: key,
          value: filters?.order[key],
        });
      }

      if (!orderFilterMounted.length) {
        orderFilterMounted.push({
          field: "installation_id",
          value: "asc",
        });
      }

      params = { ...params, where: whereFilterMounted, order: orderFilterMounted };
      const InstallationData = isArrayValid(req.idsToRequest)
        ? await new BaseService().post<{ installations: IInstallation[] }, Record<string, any>>(
            "/filtered_installations/",
            params
          )
        : { installations: [] };

      let customerRet: CustomerModel[] = req.useCustomers.map((customer) => {
        const { customer_id, customer_name, notificationRuleList } = customer;

        const installationsToAppend = req.resultToAppend
          ? req.resultToAppend.filter((result) => result.customer_id == customer_id)[0]
              .installations || []
          : [];

        return {
          customer_id,
          customer_name,
          notificationRuleList,
          installations: [
            ...InstallationData?.installations?.filter((x) =>
              customer.installationIds.includes(x.installation_id)
            ),
            ...installationsToAppend,
          ] || [...installationsToAppend],
        };
      });

      customerRet = sortBy(customerRet, "customer_name")?.map((x) => {
        return { ...x, installations: sortBy(x.installations, "product_code") };
      });

      const storage = new SessionCache();
      storage.set("customers", customerRet);
      return customerRet;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("CustomerService -> doLogin -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async fetchInstallations(): Promise<any[] | undefined> {
    try {
      const cache = new SessionCache();

      let cachedUser: User | null = cache.get("user");

      if (!cachedUser) {
        const requestUserData = await new UserService().fetch();

        if (requestUserData) {
          cachedUser = requestUserData;
        }
      }

      if (cachedUser && isArrayValid(cachedUser.installationGroup)) {
        const useInstallationGroups = cachedUser.installationGroup?.filter(
          (x) =>
            isArrayValid(x.installationList) &&
            (x.expirationDate ? new Date(x.expirationDate) > new Date() : x.expirationDate === null)
        );
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const newCustomers = useInstallationGroups!.map((x) => {
          return {
            customer_id: x.id.toString(),
            customer_name: x.name || "",
            notificationRuleList: x.notificationRuleList,
            installationIds: x.installationList.map((x) => x?.installationId?.toString() ?? ""),
          };
        });

        return newCustomers;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("CustomerService -> doLogin -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async fetchAll(filters?: AllInstallationsFilter): Promise<CustomerModel[] | undefined> {
    try {
      const cache = new SessionCache();

      let useCustomers: {
        customer_id: string;
        customer_name: string;
        expired_date?: string;
        notificationRuleList?: NotificationRuleModel[];
        installationIds: string[];
      }[] = [];

      let cachedUser: User | null = cache.get("user");

      if (!cachedUser) {
        const requestUserData = await new UserService().fetch();

        if (requestUserData) {
          cachedUser = requestUserData;
        }
      }

      if (cachedUser && isArrayValid(cachedUser.installationGroup)) {
        const useInstallationGroups = cachedUser.installationGroup?.filter((x) =>
          isArrayValid(x.installationList)
        );
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const newCustomers = useInstallationGroups!.map((x) => {
          return {
            customer_id: x.id.toString(),
            customer_name: x.name || "",
            expired_date: x.expirationDate,
            notificationRuleList: x.notificationRuleList,
            installationIds: x.installationList.map((x) => x?.installationId?.toString() ?? ""),
          };
        });

        useCustomers = newCustomers;
      }

      const idsToRequestAux: number[] = useCustomers.flatMap((c) => {
        return c.installationIds.map((id) => {
          return +id;
        });
      });

      const mergeFilter = filters ? filters : {};
      const auxParams = {
        ...mergeFilter,
        installation_ids: idsToRequestAux,
        return_ids: false,
        page_size: idsToRequestAux.length,
      } as AllInstallationsFilter;

      const InstallationData: any[] = await new InstallationService().filterNew(auxParams);
      // const InstallationData = isArrayValid(idsToRequest)
      //   ? await new BaseService().post<
      //       { installations: IInstallation[] },
      //       { installation_id: string }[]
      //     >("/installation/", idsToRequest)
      //   : { installations: [] };

      let customerRet: CustomerModel[] = useCustomers.map((customer) => {
        const { customer_id, customer_name, expired_date, notificationRuleList } = customer;

        return {
          customer_id,
          customer_name,
          expired_date,
          notificationRuleList,
          installations:
            InstallationData.filter((x) => customer.installationIds.includes(x.installation_id)) ||
            [],
        };
      });

      customerRet = sortBy(customerRet, "customer_name")?.map((x) => {
        return { ...x, installations: sortBy(x.installations, "product_code") };
      });

      const storage = new SessionCache();
      storage.set("customers", customerRet);
      return customerRet;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("CustomerService -> doLogin -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async fetchAllCacheable(filters?: AllInstallationsFilter): Promise<CustomerModel[] | undefined> {
    try {
      const customers = await new CustomerService().fetchAll(filters);
      const cacheableCustomers = customers?.filter((x) =>
        x.expired_date ? new Date(x.expired_date) > new Date() : x.expired_date === null
      );

      return cacheableCustomers;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("CustomerService -> fetchAllCacheable -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }
}
